import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, resolveImg } from "@/App";
import SafeImg from "@/components/SafeImg";
import Seo from "@/components/Seo";

export default function Home() {
  const [guests, setGuests] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/guests`),
      axios.get(`${API}/guests/years`),
      axios.get(`${API}/upcoming`),
    ]).then(([g, y, u]) => {
      setGuests(g.data);
      setYears(y.data);
      // All future upcoming, sorted
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const future = u.data.filter((item) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(item.date);
        if (!m) return false;
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return d >= today;
      });
      future.sort((a, b) => a.date.localeCompare(b.date));
      setUpcoming(future);
      setLoading(false);
    });
  }, []);

  // Featured recent guests (last 10 by year)
  const recent = [...guests].sort((a,b) => (b.year || 0) - (a.year || 0)).slice(0, 10);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-2 md:pt-6 pb-8 md:pb-12">
        <div className="anim-fadeup">
          <span className="eyebrow">
            <span className="decor-line"></span>
            Sevilla · Est. 1 de diciembre de 2014
          </span>
        </div>
        <div className="mt-4 md:mt-6 grid lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-20 items-end">
          <h1 className="text-[14vw] md:text-[84px] lg:text-[112px] leading-[0.92] font-serif font-light tracking-tight anim-fadeup" style={{animationDelay: "0.1s"}}>
            Un archivo<br />
            <em className="text-[var(--gold-deep)]">vivo</em> de<br />
            pensamiento.
          </h1>
          <div className="anim-fadeup" style={{animationDelay: "0.25s"}}>
            <p className="prose-bio max-w-md text-[15px]">
              La <strong>Tertulia In Dubio</strong> (Tertulia Social de Sevilla) la componen profesionales de muy diversa procedencia —ingenieros, abogados, notarios, jueces, profesores universitarios, economistas, inspectores de trabajo, arqueólogas, psicólogos, banqueros— todos ellos inquietos y preocupados por los problemas sociales que vive nuestra sociedad en la actualidad.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/invitados" className="btn-ghost" data-testid="cta-invitados">
                Ver archivo de invitados →
              </Link>
              <Link to="/aniversario" className="btn-ghost" style={{borderColor: "var(--gold-deep)", color: "var(--gold-deep)"}} data-testid="cta-aniversario">
                X Aniversario
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="hairline my-4"></div>
      </div>

      {/* Next tertulias carousel - compact rotating banner */}
      {upcoming.length > 0 && <UpcomingCarousel items={upcoming} />}

      {/* Recent guests preview */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="recent-section">
        <div className="flex items-end justify-between mb-8 md:mb-10 flex-wrap gap-4">
          <div>
            <span className="eyebrow">Los más recientes</span>
            <h2 className="font-serif text-5xl md:text-6xl mt-3 leading-none">Últimos invitados.</h2>
          </div>
          <Link to="/invitados" className="text-[11px] uppercase tracking-[0.22em] border-b border-[var(--gold)] pb-0.5 hover:text-[var(--gold-deep)]">
            Archivo completo ({guests.length}) →
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
            {Array.from({length:10}).map((_,i) => (<div key={i} className="aspect-[3/4] bg-[rgba(18,18,18,0.05)] animate-pulse" />))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-5 md:gap-6" data-testid="recent-grid">
            {recent.map((g, i) => (
              <Link key={g.id} to={`/invitados/${g.slug}`} className="group block anim-fadeup"
                style={{ animationDelay: `${i*0.05}s` }} data-testid={`recent-card-${g.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden border border-[rgba(255,255,255,0.7)] shadow-[0_14px_40px_-20px_rgba(22,40,60,0.22)] transition-all duration-500 group-hover:shadow-[0_30px_70px_-25px_rgba(22,40,60,0.35)]">
                  <div className="absolute inset-0 z-10 pointer-events-none transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                    style={{ boxShadow: "inset 0 0 0 1px rgba(201,167,74,0.7), 0 0 30px -6px rgba(152,197,220,0.35)" }}></div>
                  <SafeImg src={g.image} alt={g.name} name={g.name} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.05]"
                    style={{ filter: "saturate(0.88) contrast(1.04)" }} />
                  <div className="absolute inset-0 z-20 flex flex-col justify-end p-4 bg-gradient-to-t from-[rgba(10,10,10,0.85)] via-[rgba(10,10,10,0.35)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                    <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1">{g.year}</span>
                    <h3 className="font-serif text-[22px] text-white leading-[1.02]">{g.name}</h3>
                    <p className="text-[11px] text-white/75 line-clamp-2 mt-1 font-light">{g.role}</p>
                  </div>
                </div>
                <h3 className="font-serif text-[17px] mt-3 leading-tight group-hover:text-[var(--gold-deep)] transition-colors">{g.name}</h3>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mt-1 line-clamp-1">{g.role}</p>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Years navigation */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10" data-testid="years-section">
        <div className="glass p-8 md:p-12">
          <span className="eyebrow">Explora por años</span>
          <h3 className="font-serif text-4xl md:text-5xl mt-3 mb-6 leading-none">Once años. Once capítulos.</h3>
          <p className="prose-bio max-w-2xl mb-8">
            Cada año de la tertulia es un capítulo vivo del pensamiento social sevillano. Selecciona un año para conocer quiénes visitaron nuestra mesa.
          </p>
          <div className="flex flex-wrap gap-2">
            {years.map((y) => (
              <Link key={y.year} to={`/invitados?year=${y.year}`} className="px-4 py-2 border border-[var(--line)] hover:border-[var(--gold)] hover:bg-white/60 transition-all" data-testid={`year-chip-${y.year}`}>
                <span className="font-serif text-xl text-[var(--text-ink)]">{y.year}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold-deep)] ml-2">{y.count} inv.</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid md:grid-cols-[1fr_1.3fr] gap-12 items-start">
          <div>
            <span className="eyebrow">Compromiso con el debate social</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.1]">
              Una década de pensamiento crítico y construcción de ideas.
            </h2>
          </div>
          <div>
            <p className="prose-bio">
              El <strong>1 de diciembre de 2014</strong>, un grupo de profesionales inquietos, apasionados por el conocimiento y con un firme compromiso social, fundó Tertulia In Dubio. Desde aquel primer encuentro, nuestra misión ha sido clara: crear un espacio de reflexión abierto donde el pensamiento crítico y el debate sereno sean la base para construir una sociedad mejor.
            </p>
            <p className="prose-bio mt-5">
              Hoy celebramos con orgullo más de <strong>diez años</strong> de trayectoria. Nos hemos consolidado como un foro de diálogo de referencia, acogiendo voces de distintos ámbitos y ofreciendo un lugar donde la pluralidad y la inteligencia colectiva han sido el motor de nuestras conversaciones.
            </p>
            <div className="mt-8 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
              {["Derecho", "Ciencia", "Política", "Cultura", "Economía", "Tecnología", "Filosofía", "Medio ambiente"].map((t) => (
                <span key={t} className="px-3 py-1.5 border border-[var(--line)]">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Nacimiento · Los porqués */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="home-nacimiento">
        <div className="text-center mb-10">
          <span className="eyebrow">
            <span className="decor-line"></span>
            Nacimiento · Los porqués
          </span>
          <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-[1.05]">
            Por qué nació <em className="text-[var(--gold-deep)]">In Dubio</em>.
          </h2>
        </div>
        <div className="glass p-8 md:p-14">
          <div className="prose-bio text-[15.5px] leading-[1.85]" style={{fontFamily: "'Cormorant Garamond', serif", fontSize: "19px", fontWeight: 300, fontStyle: "italic"}}>
            <p style={{textIndent: "0"}}>«Tras la integración de ASNALA-Andalucía en su asociación nacional, con la consiguiente disolución de sus órganos autonómicos, los miembros de la directiva local de esta asociación coincidimos en la necesidad de mantener la iniciativa social en un ámbito más diverso y abierto que el del derecho laboral. Por esta razón, nos concitamos el <strong>1 de diciembre de 2014</strong>, hace aproximadamente tres años, a fin de crear esta tertulia, propuesta inicial de <strong>Julio Díaz</strong> secundada posteriormente por <strong>Fernando Azcona, José Luis Vilaplana y Ernesto A. Holgado</strong>.</p>
            <p>La propuesta consistía en reunir a profesionales de distintos sectores de actividad de Andalucía, con el fin de abrirnos a las realidades de las diferentes circunstancias sociales que marcan el día a día de nuestro entorno, Sevilla, Andalucía y España. La invitación nació abierta a personas de distintos sectores de nuestra sociedad, siempre con un marcado carácter social y con el compromiso de servir abierta y sinceramente al diálogo, la ecuanimidad, la concordia, la tolerancia y el respeto a todas las ideas.</p>
            <p>Con una buena acogida entre distintos profesionales de diferentes entornos de actividad, actualmente la Tertulia In Dubio (Tertulia Social de Sevilla) se integra por un grupo de unas 25 personas que trimestralmente nos citamos en un ambiente distendido y abierto, a fin de tratar temas sociales, culturales, políticos, profesionales, económicos, artísticos y de actualidad, con distintos agentes de nuestra sociedad, siempre desde la admiración y el respeto a todos los invitados que coinciden en nuestra mesa de debate.»</p>
          </div>
          <div className="mt-8 pt-6 border-t border-[var(--line)] text-right">
            <p className="font-serif text-xl text-[var(--gold-deep)]">— Ernesto Holgado Ramos</p>
            <p className="eyebrow mt-1">Abogado · Miembro Fundador</p>
          </div>
        </div>
      </section>
    </div>
  );
}

const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function UpcomingCarousel({ items }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length < 2) return;
    const timer = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(timer);
  }, [items.length, paused]);

  const go = (dir) => setIdx((i) => (i + dir + items.length) % items.length);

  return (
    <section
      className="max-w-[1600px] mx-auto px-4 md:px-10 py-4 md:py-6"
      data-testid="home-upcoming-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="relative overflow-hidden rounded-sm"
        style={{ background: "linear-gradient(135deg, #1a2f52 0%, #243a6b 55%, #2c4b7f 100%)" }}
      >
        <div className="absolute top-0 left-0 w-[2px] h-full" style={{background: "linear-gradient(to bottom, transparent, var(--gold), transparent)"}}></div>
        <div className="absolute -top-16 -right-16 w-[280px] h-[280px] rounded-full opacity-15 pointer-events-none" style={{background: "radial-gradient(circle, rgba(201,167,74,0.5), transparent 60%)"}}></div>

        {/* Slides */}
        <div className="relative">
          {items.map((item, i) => (
            <UpcomingSlide key={item.id} item={item} active={i === idx} />
          ))}
        </div>

        {/* Navigation arrows (only if >1 item) */}
        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/25 text-white/70 hover:text-white hover:border-white/60 text-lg leading-none flex items-center justify-center transition-all"
              aria-label="Anterior"
              data-testid="carousel-prev"
            >‹</button>
            <button
              type="button"
              onClick={() => go(1)}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/25 text-white/70 hover:text-white hover:border-white/60 text-lg leading-none flex items-center justify-center transition-all"
              aria-label="Siguiente"
              data-testid="carousel-next"
            >›</button>
            {/* Dots */}
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-2" data-testid="carousel-dots">
              {items.map((it, i) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`h-[3px] rounded-full transition-all ${i === idx ? "w-6 bg-[var(--gold)]" : "w-3 bg-white/30 hover:bg-white/60"}`}
                  aria-label={`Ir a tertulia ${i+1}`}
                  data-testid={`carousel-dot-${i}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function UpcomingSlide({ item, active }) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(item.date);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
  const daysLeft = d ? Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const weekday = d ? ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"][d.getDay()] : "";

  return (
    <Link
      to="/proximas"
      className={`block px-5 md:px-10 py-4 md:py-5 text-white transition-opacity duration-700 ${active ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}
      data-testid={`carousel-slide-${item.id}`}
    >
      <div className="flex items-center gap-4 md:gap-8 min-h-[78px] md:min-h-[90px]">
        {/* Compact date */}
        <div className="flex-shrink-0 border-r border-white/15 pr-4 md:pr-8 text-center min-w-[70px] md:min-w-[88px]">
          {d ? (
            <>
              <div className="text-[9px] md:text-[10px] uppercase tracking-[0.22em] text-white/55">{weekday}</div>
              <div className="font-serif text-[38px] md:text-[46px] leading-none text-white">{d.getDate()}</div>
              <div className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] capitalize mt-0.5">{MONTHS_ES[d.getMonth()].slice(0,3)} {String(d.getFullYear()).slice(2)}</div>
            </>
          ) : (
            <div className="font-serif text-xl text-[var(--gold)]">{item.date}</div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <span className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-[var(--gold)]">Próxima tertulia{daysLeft > 0 && daysLeft < 90 ? ` · en ${daysLeft} días` : ""}</span>
          <h3 className="font-serif text-xl md:text-3xl mt-0.5 leading-[1.05] text-white truncate">
            {item.guest_name}
          </h3>
          {item.guest_role && (
            <p className="text-[11px] md:text-[12.5px] text-white/65 mt-0.5 line-clamp-1">{item.guest_role}</p>
          )}
        </div>

        {/* CTA - visible only on desktop */}
        <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[var(--gold)] flex-shrink-0">
          Ver calendario
          <span>→</span>
        </div>
      </div>
    </Link>
  );
}
